import { useDisclosure } from '@mantine/hooks';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { InstanceDTO } from 'gen/model';
import { useState } from 'react';
import useSWR from 'swr';

import AvatarImageWrapper from '@ui/Components/Avatars/AvatarImageWrapper';
import CustomNoRowsOverlay from '@ui/Components/DataGrid/CustomNoRowsOverlay';
import { UploadType } from '@ui/Components/Image/UploadType';
import DescriptionModal from '@ui/Components/Modals/DescriptionModal';
import { Advertised, InstanceDescription, InstanceInfo } from '@ui/Types/AuthServer';
import Capitalize from '@ui/Utils/Capitalize';

interface NewPlacesListProps {
    instances: Omit<InstanceDTO, 'cloak_role'>[];
}

interface NewPlaceCardProps {
    instance: Omit<InstanceDTO, 'cloak_role'>;
}

function NewPlaceCard({ instance }: Readonly<NewPlaceCardProps>) {
    const theme = useTheme();

    // Data fetching
    const { data: instanceDescription } = useSWR<InstanceDescription>(
        instance.domain ? ['tasks/instance_description', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );
    const { data: instanceInfo } = useSWR<InstanceInfo>(
        instance.domain ? ['tasks/instance_info', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );
    const { data: advertised } = useSWR<Advertised>(
        instance.domain ? ['users/advertised', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );

    // Modal state for DescriptionModal
    const [descriptionModalOpen, { close: handleCloseDescriptionModal, open: handleOpenDescriptionModal }] =
        useDisclosure(false);

    const name = instanceDescription?.name ?? instance.name;
    const type = instanceDescription?.instance_type ? Capitalize(instanceDescription.instance_type) : 'Location';
    const address = instanceDescription?.address ? Capitalize(instanceDescription.address) : 'Address not available';
    const description = instanceDescription?.description ?? 'No description available';
    const isOnline = !!instanceInfo;
    const isPublic = instanceDescription?.public_operator ?? false;
    const imagePath = instanceDescription?.image;

    return (
        <Card
            sx={{
                '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-1px)',
                },
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s ease-in-out',
            }}
        >
            {/* Status indicator */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    zIndex: 2,
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        backdropFilter: 'blur(8px)',
                        backgroundColor: alpha(theme.palette.common.black, 0.6),
                        borderRadius: 2,
                        display: 'flex',
                        gap: 1,
                        p: 1,
                    }}
                >
                    <Chip
                        icon={isPublic ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                        label={isPublic ? 'Public' : 'Private'}
                        size="small"
                        sx={{
                            '& .MuiChip-icon': {
                                color: 'inherit',
                            },
                            backgroundColor: isPublic
                                ? alpha(theme.palette.success.main, 0.9)
                                : alpha(theme.palette.warning.main, 0.9),
                            borderRadius: 2,
                            color: theme.palette.common.white,
                        }}
                        variant="filled"
                    />
                    <Box
                        sx={{
                            backgroundColor: isOnline ? theme.palette.success.main : theme.palette.error.main,
                            border: `2px solid ${theme.palette.common.white}`,
                            borderRadius: '50%',
                            boxShadow: theme.shadows[2],
                            height: 12,
                            width: 12,
                        }}
                    />
                </Box>
            </Box>

            {/* Image section */}
            <Box
                sx={{
                    aspectRatio: '16/9',
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                }}
            >
                <AvatarImageWrapper
                    imagePath={imagePath}
                    imageType={UploadType.Instance}
                    isLoading={false}
                    sx={{
                        '& img': {
                            objectFit: 'cover',
                        },
                        height: '100%',
                        width: '100%',
                    }}
                    variant="square"
                />
                <Box
                    sx={{
                        alignItems: 'flex-end',
                        background: `linear-gradient(transparent, ${alpha(theme.palette.common.black, 0.7)})`,
                        bottom: 0,
                        display: 'flex',
                        height: 60,
                        left: 0,
                        p: 2,
                        position: 'absolute',
                        right: 0,
                    }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        }}
                        variant="h6"
                    >
                        {name}
                    </Typography>
                </Box>
            </Box>

            {/* Content section */}
            <Box sx={{ p: 2, pb: 0 }}>
                {/* Type and Address */}
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                    <LocationOnIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                    <Typography sx={{ color: theme.palette.text.secondary }} variant="body2">
                        {type} • {address}
                    </Typography>
                </Stack>

                {/* Description */}
                <Typography
                    sx={{
                        color: theme.palette.text.secondary,
                        display: '-webkit-box',
                        lineHeight: 1.4,
                        mb: 2,
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                    }}
                    variant="body2"
                >
                    {description}
                </Typography>

                <Divider sx={{ mb: 2 }} />
            </Box>

            {/* Action buttons */}
            <CardContent sx={{ p: 2, pt: 0 }}>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Tooltip title="View information">
                        <IconButton
                            onClick={handleOpenDescriptionModal}
                            sx={{
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                },
                                color: theme.palette.info.main,
                            }}
                        >
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </CardContent>

            {/* Description Modal */}
            {instanceDescription?.name ? (
                <DescriptionModal
                    advertised={advertised}
                    domain={instance.domain}
                    excludedKeys={['id', 'public_operator']}
                    instanceDescription={instanceDescription}
                    instanceId={instance.id}
                    instanceInfo={instanceInfo}
                    onClose={handleCloseDescriptionModal}
                    open={descriptionModalOpen}
                    showJoin
                    showUserAmount={false}
                    tabs
                />
            ) : null}
        </Card>
    );
}

export default function NewPlacesList({ instances }: Readonly<NewPlacesListProps>) {
    if (instances.length === 0) {
        return <CustomNoRowsOverlay text="No places found." />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                        lg: 'repeat(3, 1fr)',
                        md: 'repeat(3, 1fr)',
                        sm: 'repeat(2, 1fr)',
                        xs: '1fr',
                    },
                }}
            >
                {instances.map((instance) => (
                    <NewPlaceCard instance={instance} key={instance.id} />
                ))}
            </Box>
        </Box>
    );
}
